import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import useBackHandler from '../hooks/useBackHandler';
import useRequestGuard from '../hooks/useRequestGuard';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import Dialog from '../components/StableDialog';
import LoadingLabel from '../components/LoadingLabel';
import PageLoading from '../components/PageLoading';
import { isOral } from '../utils/intakeForm';
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Fab, DialogActions, DialogContent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import MedImage from '../components/MedImage';
import PrnLogSheet from '../components/PrnLogSheet';
import PrnStatsDetail from '../components/PrnStatsDetail';
import AppToast from '../components/AppToast';
import {
    getDeviceUuid, getMedications, getStats,
    takeAsNeeded, deleteAsNeededLog, updateAsNeededLogTakenAt,
} from '../api';
import { syncWidget } from '../utils/widgetSync';
import useMedicationAddGate from '../hooks/useMedicationAddGate';
import { clearMedicationCaches } from '../utils/localCache';

function pad(n) { return String(n).padStart(2, '0'); }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function getToday() { return toDateStr(new Date()); }
function getDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return toDateStr(d); }


function doneText(intakeForm) {
    return isOral(intakeForm) ? translationKey('먹었어요') : translationKey('사용했어요');
}

export function formatLastTaken(lastTakenAt, intakeForm = 'ORAL') {
    const done = localize(doneText(intakeForm));
    if (!lastTakenAt) return isOral(intakeForm) ? localize("먹은 후에 눌러주세요") : localize("사용한 후에 눌러주세요");
    const then = new Date(lastTakenAt);
    const now = new Date();
    const diff = (now - then) / 1000;
    if (diff < 300) return localize("방금 전 {v0}", { v0: done });
    const sameDay = then.toDateString() === now.toDateString();
    if (sameDay) return localize("오늘 {v0}:{v1} {v2}", { v0: pad(then.getHours()), v1: pad(then.getMinutes()), v2: done });
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (then.toDateString() === yesterday.toDateString()) {
        return localize("어제 {v0}:{v1} {v2}", { v0: pad(then.getHours()), v1: pad(then.getMinutes()), v2: done });
    }
    const days = Math.floor((now - then) / 86400000);
    if (days < 30) return localize("{v0}일 전 {v1}", { v0: days, v1: done });
    const months = Math.floor(days / 30);
    if (months < 12) return localize("{v0}달 전 {v1}", { v0: months, v1: done });
    return localize("{v0}년 전 {v1}", { v0: Math.floor(months / 12), v1: done });
}

// PRN 상세에 보여줄 기간을 stats 호출 범위로 변환
function periodToRange(period) {
    const today = getToday();
    if (period === '90') return { startDate: getDaysAgo(89), endDate: today };
    if (period === 'year') return { startDate: `${new Date().getFullYear()}-01-01`, endDate: today };
    return { startDate: getDaysAgo(29), endDate: today };
}

export default function PrnPage() {
    const navigate = useNavigate();
    const beginRequest = useRequestGuard('prn-page');
    const { openMedicationAdd, MedicationAddGateDialog, checkingMedicationLimit } = useMedicationAddGate();
    const [searchParams] = useSearchParams();
    const welcomeMedId = searchParams.get('welcome');
    const goHome = useCallback(() => {
        navigate('/', { replace: true });
    }, [navigate]);

    const [medications, setMedications] = useState([]);
    const [statsByPeriod, setStatsByPeriod] = useState({}); // { period: statsResponse }
    const [period, setPeriod] = useState('30');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [processingIds, setProcessingIds] = useState(new Set());
    const [expandedMedId, setExpandedMedId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // 시트 상태
    const [logSheet, setLogSheet] = useState({ open: false, mode: 'create', med: null, logId: null, date: null, time: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, logId: null });

    const fetchAll = useCallback(async () => {
        const isCurrent = beginRequest('load');
        const medicationsCurrent = beginRequest('medications');
        const statsCurrent = beginRequest(`stats-${period}`);
        setLoading(true);
        try {
            const uuid = getDeviceUuid();
            const { startDate, endDate } = periodToRange(period);
            const [meds, stats] = await Promise.all([
                getMedications(uuid),
                getStats(uuid, startDate, endDate),
            ]);
            if (!isCurrent()) return;
            if (medicationsCurrent()) setMedications(meds || []);
            if (statsCurrent()) setStatsByPeriod(prev => ({ ...prev, [period]: stats }));
            setLoadError(false);
        } catch (err) {
            console.error('Failed to load PRN page:', err);
            if (isCurrent()) setLoadError(true);
        } finally {
            if (isCurrent()) setLoading(false);
        }
    }, [period, beginRequest]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    useBackHandler(true, goHome, 0);

    // welcome 쿼리 처리 — 신규 등록 직후 토스트 + 카드 하이라이트
    const [welcomeToast, setWelcomeToast] = useState(false);
    useEffect(() => {
        if (!welcomeMedId) return;
        setWelcomeToast(true);
        const t = setTimeout(() => {
            setWelcomeToast(false);
            // URL에서 쿼리 제거 (히스토리에 남기지 않음)
            const next = new URLSearchParams(searchParams);
            next.delete('welcome');
            navigate({ pathname: '/prn', search: next.toString() ? `?${next}` : '' }, { replace: true });
        }, 3000);
        return () => clearTimeout(t);
    }, [welcomeMedId, searchParams, navigate]);

    const refetchStats = useCallback(async () => {
        const isCurrent = beginRequest(`stats-${period}`);
        const uuid = getDeviceUuid();
        const { startDate, endDate } = periodToRange(period);
        try {
            const stats = await getStats(uuid, startDate, endDate);
            if (isCurrent()) setStatsByPeriod(prev => ({ ...prev, [period]: stats }));
        } catch {}
    }, [period, beginRequest]);

    const openCreateSheet = (med) => {
        setLogSheet({ open: true, mode: 'create', med, logId: null, date: null, time: null });
    };

    const openEditSheet = (med, log) => {
        const d = new Date(log.takenAt);
        const dateStr = toDateStr(d);
        const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setLogSheet({ open: true, mode: 'edit', med, logId: log.logId, date: dateStr, time: timeStr });
    };

    const closeSheet = () => setLogSheet(s => ({ ...s, open: false }));

    const handleSheetConfirm = async ({ date, time }) => {
        const { mode, med, logId } = logSheet;
        const takenAt = `${date}T${time}:00`;
        const key = `prn-${med?.id || logId}`;
        if (processingIds.has(key)) return;
        setProcessingIds(prev => new Set(prev).add(key));
        const isCurrent = beginRequest('mutation');
        try {
            const uuid = getDeviceUuid();
            if (mode === 'create') {
                await takeAsNeeded(uuid, med.id, takenAt);
                if (!isCurrent()) return;
                setSnackbar({ open: true, message: localize(translationKey("기록했어요")), severity: 'success' });
            } else {
                await updateAsNeededLogTakenAt(uuid, logId, takenAt);
                if (!isCurrent()) return;
                setSnackbar({ open: true, message: localize("시간을 변경했어요"), severity: 'success' });
            }
            clearMedicationCaches();
            const medicationsCurrent = beginRequest('medications');
            try { const meds = await getMedications(uuid); if (isCurrent() && medicationsCurrent()) setMedications(meds); } catch {}
            if (!isCurrent()) return;
            syncWidget(uuid);
            refetchStats();
        } catch (err) {
            if (!isCurrent()) return;
            if (err.prnRecovered) {
                const medicationsCurrent = beginRequest('medications');
                try { const meds = await getMedications(getDeviceUuid()); if (isCurrent() && medicationsCurrent()) setMedications(meds); } catch {}
                if (!isCurrent()) return;
                refetchStats();
            }
            const msg = err?.response?.data?.message || (logSheet.mode === 'create' ? translationKey('기록에 실패했어요') : translationKey('변경에 실패했어요'));
            setSnackbar({ open: true, message: msg, severity: 'error' });
            throw err;
        } finally {
            setProcessingIds(prev => { const next = new Set(prev); next.delete(key); return next; });
        }
    };

    const [deleting, setDeleting] = useState(false);
    const performDelete = async () => {
        const { logId } = deleteConfirm;
        if (!logId || deleting) return;
        setDeleting(true);
        const isCurrent = beginRequest('mutation');
        try {
            const uuid = getDeviceUuid();
            await deleteAsNeededLog(uuid, logId);
            clearMedicationCaches();
            if (!isCurrent()) return;
            setSnackbar({ open: true, message: localize("기록을 삭제했어요"), severity: 'info' });
            setDeleteConfirm({ open: false, logId: null });
            const medicationsCurrent = beginRequest('medications');
            try { const meds = await getMedications(uuid); if (isCurrent() && medicationsCurrent()) setMedications(meds); } catch {}
            if (!isCurrent()) return;
            syncWidget(uuid);
            refetchStats();
        } catch {
            if (isCurrent()) setSnackbar({ open: true, message: localize("삭제에 실패했어요"), severity: 'error' });
        } finally { setDeleting(false); }
    };

    const prnMeds = medications.filter(m => m.frequencyType === 'AS_NEEDED' && !m.paused && m.active !== false);

    const stats = statsByPeriod[period];
    const asNeededByMedId = new Map();
    (stats?.asNeeded || []).forEach(a => asNeededByMedId.set(a.medicationId, a));

    // 요약: 선택 기간 내 전체 PRN 횟수
    const periodTotal = (stats?.asNeeded || []).reduce((sum, a) => sum + (a.periodTakenCount || 0), 0);
    let mostFrequent = null;
    (stats?.asNeeded || []).forEach(a => {
        if (a.periodTakenCount > 0 && (!mostFrequent || a.periodTakenCount > mostFrequent.cnt)) {
            mostFrequent = { name: a.name, cnt: a.periodTakenCount };
        }
    });
    const periodLabel = period === '90' ? translationKey('90일') : period === 'year' ? translationKey('올해') : translationKey('30일');

    if (loading && !stats) {
        return (
            <PageLoading />
        );
    }

    if (loadError) {
        return (
            <>
                <PageHeader title={localize("필요할 때 먹는 약")} back={goHome} />
                <Box sx={{ textAlign: 'center', px: 3, py: 8 }}>
                    <SingleLineText sx={{ mb: 2 }}>{localize("약과 기록을 불러오지 못했어요")}</SingleLineText>
                    <Button variant="contained" onClick={fetchAll}>{localize("다시 불러오기")}</Button>
                </Box>
            </>
        );
    }

    if (prnMeds.length === 0) {
        return (
            <>
                <PageHeader title={localize("필요할 때 먹는 약")} back={goHome} />
                <Box sx={{
                    minHeight: 'calc(100vh - 56px)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '64px 32px 80px', textAlign: 'center',
                }}>
                    <Box sx={{
                        width: 96, height: 96, borderRadius: '28px',
                        background: 'linear-gradient(135deg, #bae6fd 0%, #38bdf8 100%)',
                        boxShadow: '0 12px 28px -10px #38bdf8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative', mb: 3,
                    }}>
                        <TouchAppIcon sx={{ fontSize: 44, color: '#0c4a6e' }} />
                        <Box sx={{
                            position: 'absolute', inset: -8, borderRadius: '32px',
                            border: '2px solid #38bdf8', opacity: 0.35,
                            pointerEvents: 'none',
                        }} />
                        <Box sx={{
                            position: 'absolute', inset: -16, borderRadius: '36px',
                            border: '2px solid #38bdf8', opacity: 0.18,
                            pointerEvents: 'none',
                        }} />
                    </Box>
                    <SingleLineText sx={{
                        fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em',
                        color: '#1a1a1a', mb: 1,
                    }}>{localize("필요할 때 먹는 약이 아직 없어요")}</SingleLineText>
                    <Typography sx={{
                        fontSize: 14, fontWeight: 500, color: '#777',
                        lineHeight: 1.5, maxWidth: 280, wordBreak: 'keep-all', mb: 4,
                    }}>{localize("두통약·소화제처럼 가끔 챙기는 약을 한 번 눌러 기록해요")}</Typography>
                    <Button
                        onClick={() => openMedicationAdd('/medications/new-bundle?mode=manual&frequency=AS_NEEDED')}
                        disabled={checkingMedicationLimit}
                        startIcon={checkingMedicationLimit ? <LoadingLabel>{null}</LoadingLabel> : <AddIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            bgcolor: '#ffe566', color: '#5a4a00',
                            fontSize: 15, fontWeight: 800,
                            borderRadius: '999px', padding: '14px 28px',
                            boxShadow: '0 6px 14px -6px rgba(255,229,102,0.7)',
                            '&:hover': { bgcolor: '#e8d040' },
                            '&:active': { transform: 'scale(0.98)' },
                            textTransform: 'none',
                        }}
                    >{localize("필요할 때 먹는 약 추가")}</Button>
                </Box>
                <MedicationAddGateDialog />
            </>
        );
    }

    return (
        <>
            <PageHeader title={localize("필요할 때 먹는 약")} back={goHome} />

            {/* 신규 등록 환영 토스트 */}
            <AppToast
                open={welcomeToast}
                severity="success"
                message={localize("추가했어요! 먹을 때마다 카드를 눌러 기록하세요")}
                top="calc(56px + env(safe-area-inset-top, 0px))"
            />

            {/* 요약 카드 */}
            <Box sx={{
                mx: 2, mt: 1.5, mb: 1.5, p: '14px 16px',
                position: 'relative', overflow: 'hidden',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '1px solid #bae6fd',
            }}>
                <Box sx={{
                    position: 'absolute', right: -20, top: -30,
                    width: 90, height: 90, borderRadius: '50%',
                    bgcolor: '#bae6fd', opacity: 0.4,
                    pointerEvents: 'none',
                }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#3f6c8a' }}>{readAppLanguage() === 'ko' ? `${localize('최근 ')}${localize(periodLabel)}` : localize(period === 'year' ? '올해' : period === '90' ? '최근 90일' : '최근 30일')}
                    </Typography>
                    <Typography sx={{
                        fontSize: 22, fontWeight: 800, color: '#0c4a6e',
                        lineHeight: 1.2, mt: '2px',
                    }}>
                        {localize('{count}회 기록', { count: periodTotal })}</Typography>
                    {mostFrequent && (
                        <Typography sx={{
                            fontSize: 13, fontWeight: 500, color: '#3f6c8a', mt: '4px',
                        }}>{localize("가장 자주 먹은 약 · ")}<strong style={{ color: '#0c4a6e' }}>{mostFrequent.name}</strong>
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* 약 목록 — 카드 + 펼침 통계 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2 }}>
                {prnMeds.map(med => {
                    const key = `prn-${med.id}`;
                    const processing = processingIds.has(key);
                    const expanded = expandedMedId === med.id;
                    const stat = asNeededByMedId.get(med.id);
                    const isWelcome = welcomeMedId && String(med.id) === String(welcomeMedId) && welcomeToast;
                    return (
                        <Box key={med.id} sx={{
                            borderRadius: '16px',
                            bgcolor: '#fff',
                            border: isWelcome ? '2px solid #38bdf8' : '1px solid #f0f0f0',
                            transition: 'border-color 200ms ease',
                            overflow: 'hidden',
                        }}>
                            <Box sx={{
                                p: '14px 16px',
                                display: 'flex', alignItems: 'center', gap: 1.5,
                            }}>
                                <MedImage src={med.imageUrl} size={48} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography data-medication-name-preview sx={{
                                        fontWeight: 700, fontSize: 16, color: '#1a1a1a',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>{med.name}</Typography>
                                    <Typography sx={{ fontSize: 13, color: '#777', mt: '2px' }}>
                                        {formatLastTaken(med.lastTakenAt, med.intakeForm)}
                                    </Typography>
                                </Box>
                                <Button
                                    disabled={processing}
                                    onClick={() => openCreateSheet(med)}
                                    sx={{
                                        bgcolor: '#ffe566', color: '#5a4a00',
                                        fontSize: 13, fontWeight: 800,
                                        borderRadius: '999px', padding: '10px 14px',
                                        minHeight: 44,
                                        flexShrink: 0,
                                        boxShadow: '0 2px 8px rgba(230,168,0,0.3)',
                                        '&:hover': { bgcolor: '#e8d040' },
                                        '&:active': { transform: 'scale(0.98)' },
                                        '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                                        textTransform: 'none',
                                    }}
                                >{localize("기록하기")}</Button>
                            </Box>
                            {/* 펼침 토글 */}
                            <Box
                                component="button"
                                type="button"
                                onClick={() => setExpandedMedId(expanded ? null : med.id)}
                                aria-label={expanded ? localize("상세 접기") : localize("상세 펼치기")}
                                sx={{
                                    width: '100%', border: 'none', cursor: 'pointer',
                                    bgcolor: '#f9fafb',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                    py: '6px',
                                    color: '#6B7280', fontSize: 12, fontWeight: 700,
                                    '&:active': { bgcolor: '#f1f5f9' },
                                }}
                            >
                                <InsightsIcon sx={{ fontSize: 14, color: '#9333ea' }} />
                                {expanded ? localize("상세 접기") : localize("상세 통계")}
                                {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                            </Box>
                            {expanded && (
                                <Box sx={{ p: '12px 16px 16px', bgcolor: '#fafbfc' }}>
                                    <PrnStatsDetail
                                        stat={stat}
                                        period={period}
                                        onPeriodChange={(p) => setPeriod(p)}
                                        onEditLog={(log) => openEditSheet(med, log)}
                                        onDeleteLog={(log) => setDeleteConfirm({ open: true, logId: log.logId })}
                                    />
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ height: 80 }} />

            <Fab disabled={checkingMedicationLimit} variant="extended" onClick={() => openMedicationAdd('/medications/new-bundle?mode=manual&frequency=AS_NEEDED')}
                sx={{
                    position: 'fixed', bottom: 80,
                    right: 'calc(50% - min(240px, 50vw) + 20px)',
                    bgcolor: '#ffe566', color: '#5a4a00',
                    '&:hover': { bgcolor: '#e8d040' },
                    boxShadow: '0 4px 12px rgba(230,168,0,0.4)',
                    fontWeight: 700, fontSize: '0.95rem', gap: 0.5,
                    borderRadius: '28px', px: 2.5,
                }}>
                <AddIcon sx={{ fontSize: 20 }} />
                {checkingMedicationLimit ? <LoadingLabel /> : localize("약 추가")}
            </Fab>

            <PrnLogSheet
                open={logSheet.open}
                mode={logSheet.mode}
                medication={logSheet.med}
                initialDate={logSheet.date}
                initialTime={logSheet.time}
                onClose={closeSheet}
                onConfirm={handleSheetConfirm}
            />

            <Dialog open={deleteConfirm.open} onClose={() => { if (!deleting) setDeleteConfirm({ open: false, logId: null }); }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, pb: 0.5 }}>{localize("기록을 삭제할까요?")}</DialogTitle>
                <DialogContent>
                    <SingleLineText sx={{ fontSize: '1.05rem', color: '#777' }}>{localize("삭제하면 되돌릴 수 없어요.")}</SingleLineText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
                    <Button disabled={deleting} onClick={() => setDeleteConfirm({ open: false, logId: null })}
                        variant="outlined"
                        sx={{ color: '#666', borderColor: '#e5e7eb', fontWeight: 600, fontSize: '1.05rem', borderRadius: 2, flex: 1 }}
                    >{localize("아니요")}</Button>
                    <Button disabled={deleting} onClick={performDelete}
                        variant="contained"
                        sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '1.05rem', borderRadius: 2, flex: 1, '&:hover': { bgcolor: '#dc2626' } }}
                    >{deleting ? <LoadingLabel /> : localize("삭제할게요")}</Button>
                </DialogActions>
            </Dialog>

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'success' && snackbar.message === translationKey('기록했어요') ? 3000 : 5000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
            <MedicationAddGateDialog />
        </>
    );
}
